import { useEffect, useState } from "react";

import { HttpStatusCode } from "axios";
import {
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    useToast,
    useDisclosure,
} from "@chakra-ui/react";

import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST } from "@/common/constants";
import ModalDelete from "@/components/Modals/ModalDelete";
import TableInspectionEmails from "@/components/Tables/TableInspectionEmails";
import { useAppSelector } from "@/redux/hooks";
import { RootState } from "@/redux/store";
import { getAllInspectionEmails, resendInspectionEmail } from "@/services/inspectionEmailService";

import "../styles/ListPage.css";

const InspectionEmails = () => {
    const toast = useToast();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenPreview,
        onOpen: onOpenPreview,
        onClose: onClosePreview,
    } = useDisclosure();
    const name = useAppSelector((state: RootState) => state.account.name);

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rawDataList, setRawDataList] = useState([]) as any;
    const [hasNextPage, setHasNextPage] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [previewData, setPreviewData] = useState<any>(null);

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handlePreview = (data: any) => {
        setPreviewData(data);
        onOpenPreview();
    };

    const handleResend = async (id: string) => {
        try {
            const obj = {
                id: id,
                modifiedBy: name || "System",
            };
            const response = await resendInspectionEmail(obj) as any;

            if (response.status === HttpStatusCode.Ok) {
                await getData();
                toast({
                    title: 'Email sent.',
                    description: "The email has been sent successfully.",
                    status: 'success',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            } else {
                toast({
                    title: 'Failed to send email.',
                    description: response?.message || "We failed to send the email.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: "An error occurred while sending the email.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    const handleConfirmDelete = async () => {
        try {
            toast({
                title: 'Delete not allowed.',
                description: `Email record ${idToDelete || ''} is maintained for audit purposes.`,
                status: 'warning',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } finally {
            onClose();
        }
    };

    const handleSearch = () => {
        setSearchValue(searchInput);
        setCurrentPageNumber(DEFAULT_PAGE_NUMBER);
    };

    const getData = async () => {
        setIsLoading(true);

        const param = {
            search: searchValue,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
        };

        try {
            const response = await getAllInspectionEmails(param) as any;
            if (response.status === HttpStatusCode.Ok) {
                setRawDataList(response.data || []);
                setTotalPageNumber(response.totalPage || 0);
                setHasNextPage(response.hasNextPage || false);
            } else {
                toast({
                    title: 'Load error',
                    description: response?.message || "Failed to load inspection emails.",
                    status: 'error',
                    duration: DURATION_TOAST,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Load error',
                description: "An error occurred while loading inspection emails.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangePageNumber = (pageNumber: number) => {
        setCurrentPageNumber(pageNumber);
    };

    const handleChangePageSize = (pageSize: number) => {
        setCurrentPageSize(pageSize);
        setCurrentPageNumber(DEFAULT_PAGE_NUMBER);
    };

    useEffect(() => {
        getData();
    }, [searchValue, currentPageNumber, currentPageSize]);

    return (
        <div style={{ padding: '10px 15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', gap: '8px' }}>
                <Input
                    placeholder="Search by email type, from, to, or subject..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    width="300px"
                    borderRadius={8}
                    borderColor={COLORS.PRIMARY}
                    _focus={{ borderColor: COLORS.PRIMARY, boxShadow: `0 0 0 1px ${COLORS.PRIMARY}` }}
                />
                <button
                    onClick={handleSearch}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: COLORS.PRIMARY,
                        color: '#fff',
                        borderRadius: 8,
                        border: 'none',
                        cursor: 'pointer',
                    }}
                >
                    Search
                </button>
            </div>

            <ModalDelete
                title="Delete it?"
                isOpen={isOpen}
                onClose={onClose}
                handleConfirmDelete={handleConfirmDelete}
            />

            <Modal onClose={onClosePreview} isOpen={isOpenPreview} size='6xl' isCentered>
                <ModalOverlay />
                <ModalContent borderRadius={15} padding={2}>
                    <ModalHeader>{previewData?.subject || 'Email Content Preview'}</ModalHeader>
                    <ModalCloseButton _focus={{ outline: 'none', boxShadow: 'none' }} />
                    <ModalBody>
                        <iframe
                            title='inspection-email-preview'
                            srcDoc={previewData?.content || '<p>No content available</p>'}
                            style={{ width: '100%', minHeight: 520, border: '1px solid #EAECF0', borderRadius: 8 }}
                        />
                    </ModalBody>
                </ModalContent>
            </Modal>

            <TableInspectionEmails
                dataList={rawDataList}
                isLoading={isLoading}
                handlePreview={handlePreview}
                handleResend={handleResend}
                handleDelete={handleDelete}
                currentPageSize={currentPageSize}
                handleChangePageSize={handleChangePageSize}
                handleChangePageNumber={handleChangePageNumber}
                currentPageNumber={currentPageNumber}
                totalPageNumber={totalPageNumber}
                hasNextPage={hasNextPage}
            />
        </div>
    );
};

export default InspectionEmails;
